import React, { useState, useEffect } from "react"
import { Button, Typography, CircularProgress } from "@mui/material"
import { QrCodeScanner, ScaleOutlined } from "@mui/icons-material"
import { Dialog, Stack, CenteredBox } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"

interface QuantityInputDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (quantity: number) => void
  lotNumber: string
  loading?: boolean
}

const QuantityInputDialog: React.FC<QuantityInputDialogProps> = ({
  open,
  onClose,
  onConfirm,
  lotNumber,
  loading = false
}) => {
  const [quantity, setQuantity] = useState<number | null>(null)
  const [error, setError] = useState<string>("")

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setQuantity(null)
      setError("")
    }
  }, [open])

  const handleQuantityChange = (value: number) => {
    setQuantity(value)

    // Clear error when user starts typing
    if (error) {
      setError("")
    }
  }

  const handleConfirm = () => {
    // Validation
    if (!quantity) {
      setError("Quantity is required")
      return
    }

    if (isNaN(quantity) || quantity <= 0) {
      setError("Please enter a valid quantity greater than 0")
      return
    }

    onConfirm(quantity)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleConfirm()
    }
  }

  return (
    <Dialog open={open} toggleDialog={onClose} maxWidth="sm" fullWidth sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}>
      <Stack p={3} spacing={3}>
        <CenteredBox>
          <QrCodeScanner sx={{ fontSize: "3rem", color: "primary.main", mb: 1 }} />
          <Typography variant="h5" gutterBottom>
            Enter Quantity
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Lot Number: <strong>{lotNumber}</strong>
          </Typography>
        </CenteredBox>

        <OutlinedInput
          label="Quantity"
          type="number"
          value={quantity}
          onChange={(_, value) => handleQuantityChange(value as number)}
          onKeyDown={handleKeyPress}
          placeholder="Enter quantity"
          fullWidth
          autoFocus
          error={!!error}
          helperText={error}
          disabled={loading}
          startAdornment={<ScaleOutlined sx={{ mr: 1, color: "text.secondary" }} />}
          inputProps={{
            min: 0,
            step: "any"
          }}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={loading || !quantity}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  )
}

export default QuantityInputDialog
